import React, { useState, useRef } from "react";
import { Button, Form, Image, Input, Spin } from "antd";
import {
  CloseOutlined,
  FilePdfOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Text from "../../context/language-context";
import { eDocType, eFORMSTATUS, eResultCode } from "../../utils/enum";
import { validateFile } from "../../utils/input.validator";
import { UploadDocs } from "../../utils/api.constant";
import AuthUtil from "../../utils/auth";
import useToast from "../../hooks/useToast";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import styles from "./document-upload.module.css";

/**
 * Props for the DocumentUploadField component
 */
export interface DocumentUploadFieldProps {
  /** Form field name (e.g., "SCHOOL_2026_PT1_Q23") - used for API and form binding */
  fieldName: string;
  /** Form ID for upload API */
  formId: string;
  /** User ID for upload API */
  userId: string | number;
  /** Form stage for upload API (default: STAGE1INPROGRESS) */
  currentStage?: number;
  /** Field label - can use i18n Text component */
  label?: React.ReactNode;
  /** Document type for upload API (default: eDocType.OTHER) */
  docType?: eDocType;
  /** Accept attribute for file input (default: '.jpg,.jpeg,.png,.pdf') */
  acceptTypes?: string;
  /** Is field required (default: true) */
  required?: boolean;
  /** Disable the field */
  disabled?: boolean;
  /** Already uploaded documents to show preview */
  existingDocuments?: any[];
  /** Callback when upload completes successfully - receives the document ID */
  onUploadComplete?: (docId: string) => void;
  /** Callback when upload fails - receives error message */
  onUploadError?: (error: string) => void;

  labelStyle?: React.CSSProperties;
}

/**
 * A reusable document upload field component that handles:
 * - File validation (type and size)
 * - Uploading to the backend API
 * - Displaying preview links for uploaded documents
 * - Integration with Ant Design Form
 *
 * @example
 * ```tsx
 * <DocumentUploadField
 *   fieldName="SCHOOL_2026_PT1_Q23"
 *   label={Text({ tid: "SCHOOL_2026_PT1_Q23", def: "Upload signature" })}
 *   formId={formId || ""}
 *   userId={user?.id || ""}
 *   disabled={disabled}
 *   existingDocuments={documentsData}
 *   onUploadComplete={(docId) => handleFieldChange("SCHOOL_2026_PT1_Q23", docId)}
 * />
 * ```
 */
const DocumentUploadField: React.FC<DocumentUploadFieldProps> = ({
  fieldName,
  formId,
  userId,
  currentStage = eFORMSTATUS.STAGE1INPROGRESS,
  label,
  docType = eDocType.OTHER,
  acceptTypes = ".jpg,.jpeg,.png,.pdf",
  required = true,
  disabled = false,
  existingDocuments = [],
  onUploadComplete,
  onUploadError,
  labelStyle,
}) => {
  const { onShowToast } = useToast();
  const form = Form.useFormInstance();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // i18n text
  const PREVIEW = Text({ tid: "PREVIEW", def: "Preview" });
  const UPLOADED_DOCUMENT = Text({
    tid: "UPLOADED_DOCUMENT",
    def: "Uploaded Document",
  });
  const REQUIRED_MESSAGE = Text({ tid: "REQUIRED", def: "Required" });
  const REPLACE_FILE = Text({ tid: "REPLACE_FILE", def: "Replace File" });
  const CHOOSE_FILE = Text({ tid: "CHOOSE_FILE", def: "Choose File" });

  /**
   * Check if document already exists in existing documents
   */
  const existingDocument = existingDocuments?.find(
    (doc: any) => doc.labelName === fieldName,
  );

  /**
   * Check if a file is already uploaded (either new or existing)
   */
  const hasUploadedFile = Boolean(documentId || existingDocument);

  /**
   * Determine if field should be required based on existing documents
   */
  const isFieldRequired = required && !documentId && !existingDocument;

  /**
   * Upload document to the backend
   */
  const uploadDocument = async (file: File) => {
    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("docType", docType.toString());
      formData.append("formId", formId);
      formData.append("userId", userId.toString());
      formData.append("labelName", fieldName);
      formData.append("currentStage", currentStage.toString());

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDocs}`,
        {
          method: "POST",
          body: formData,
          headers: { ...AuthUtil.getToken() },
        },
      );

      const res = await response.json();
      const { data, dataResponse } = res;
      const { returnCode } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        const docId = data.docUrl.toString();
        setDocumentId(docId);
        onUploadComplete?.(docId);
      } else {
        const errorMessage = "Failed to upload document";
        console.error(errorMessage, response.statusText);
        onUploadError?.(errorMessage);
      }
    } catch (error) {
      const errorMessage = "An error occurred while uploading";
      console.error(error);
      onUploadError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handle file input change
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const validation = validateFile(file);

    if (!validation.valid) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: validation.error,
      });
      e.target.value = "";
      form.resetFields([fieldName]);
      return;
    }

    uploadDocument(file);
  };

  /**
   * Trigger file input click
   */
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Check if the file path is an image based on extension
   */
  const isImageFile = (path: string): boolean => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    const lowerPath = path.toLowerCase();
    return imageExtensions.some((ext) => lowerPath.endsWith(ext));
  };

  /**
   * Render preview for uploaded document
   * - Images: Show Ant Design Image with preview capability
   * - PDFs/Others: Show link to open in new tab
   */
  const renderPreview = () => {
    // Determine the document path and name
    let docPath: string | null = null;
    let docName: string | null = null;

    if (documentId) {
      docPath = documentId;
      docName = PREVIEW;
    } else if (existingDocument) {
      docPath = existingDocument.docPath;
      docName = existingDocument.docName || PREVIEW;
    }

    if (!docPath) return null;

    const fullUrl = `${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docPath}`;

    // For images, show Ant Design Image with preview
    if (isImageFile(docPath)) {
      return (
        <div className={styles.previewContainer}>
          <span>{UPLOADED_DOCUMENT}:</span>
          <Image
            src={fullUrl}
            alt={docName || "Uploaded document"}
            width={200}
            height={50}
            style={{ objectFit: "contain", borderRadius: 4 }}
            preview={{
              mask: PREVIEW,
            }}
          />
        </div>
      );
    }

    // For non-images (PDFs, etc.), show styled preview with icon
    return (
      <div className={styles.previewLink}>
        <span>{UPLOADED_DOCUMENT}:</span>
        <a
          href={fullUrl}
          target='_blank'
          rel='noreferrer'
          className={styles.pdfPreviewCard}
        >
          <FilePdfOutlined className={styles.pdfIcon} />
          <span className={styles.pdfLabel}>{docName}</span>
        </a>
      </div>
    );
  };

  return (
    <div
      className={styles.documentUploadField}
      id='document-upload-field'
    >
      <Form.Item
       
        name={fieldName}
        label={label}
        style={labelStyle ? labelStyle : {}}
        rules={[
          {
            required: isFieldRequired,
            message: REQUIRED_MESSAGE,
          },
        ]}
      >
        <Input
          type='hidden'
        />
      </Form.Item>

      <Spin spinning={isUploading}>
        {/* Hidden file input - NOT bound to Form.Item */}
        <input
          ref={fileInputRef}
          type='file'
          accept={acceptTypes}
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={disabled || isUploading}
        />

        {/* Show different UI based on whether file is uploaded */}
        {hasUploadedFile ? (
          <div className={styles.uploadedState}>
            {renderPreview()}
            {!disabled && (
              <Button
                icon={<UploadOutlined />}
                onClick={handleButtonClick}
                loading={isUploading}
                className={styles.replaceButton}
              >
                {REPLACE_FILE}
              </Button>
            )}
          </div>
        ) : (
          <Button
            icon={<UploadOutlined />}
            onClick={handleButtonClick}
            loading={isUploading}
              disabled={disabled}
              style={{
                margin: "0 0 25px 0px"
              }}
          >
            {CHOOSE_FILE}
          </Button>
        )}
      </Spin>
    </div>
  );
};

export default DocumentUploadField;
